interface IDrupalMenuLink {
  external?: boolean
  uri?: string
  alias?: string | null
  relative?: string
}

export interface IResolvedDrupalMenuLink {
  path: string
  isExternal: boolean
}

const OWN_SITE_HOST_PATTERN = /^(www\.)?sakipsabancimuzesi\.org$/i

const EXTERNAL_SAME_SITE_PATH_PREFIXES = ['/shop', '/bilet']

const normalizePath = (path: string) =>
  path.startsWith('/') ? path : `/${path}`

const stripLocalePrefix = (path: string) => {
  const match = path.match(/^\/(tr|en)(\/.*)?$/)
  if (!match) return path

  return match[2] || '/'
}

const shouldStayExternalOnSameSite = (path: string) =>
  EXTERNAL_SAME_SITE_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  )

const extractInternalPathFromUrl = (url: string): string | null => {
  try {
    const { hostname, pathname } = new URL(url)
    if (!OWN_SITE_HOST_PATTERN.test(hostname)) return null

    const normalized = normalizePath(pathname.replace(/\/$/, '') || '/')
    const withoutLocale = stripLocalePrefix(normalized)
    if (!withoutLocale || withoutLocale === '/') return null

    return withoutLocale
  } catch {
    return null
  }
}

/**
 * Drupal menu API returns `alias` only for node-based paths.
 * Frontend-only routes (e.g. /muzede-ne-var) come back with alias=null
 * and must be resolved from `relative` or `base:` uri instead.
 *
 * EN menu items are often marked external with full sakipsabancimuzesi.org
 * URLs; those are converted back to internal Next.js paths when possible.
 */
export const resolveDrupalMenuLink = (
  item: IDrupalMenuLink
): IResolvedDrupalMenuLink => {
  if (item.external) {
    const url = item.uri || item.relative || ''
    const internalPath = url ? extractInternalPathFromUrl(url) : null

    if (internalPath && !shouldStayExternalOnSameSite(internalPath)) {
      return { path: internalPath, isExternal: false }
    }

    return { path: url, isExternal: true }
  }

  if (item.alias) {
    return { path: normalizePath(item.alias), isExternal: false }
  }

  if (item.relative) {
    const relativePath = stripLocalePrefix(item.relative)
    if (relativePath && relativePath !== '/') {
      return { path: normalizePath(relativePath), isExternal: false }
    }
  }

  if (item.uri?.startsWith('base:')) {
    return { path: normalizePath(item.uri.slice(5)), isExternal: false }
  }

  if (item.uri?.startsWith('internal:')) {
    return {
      path: normalizePath(item.uri.replace(/^internal:/, '')),
      isExternal: false,
    }
  }

  return { path: '', isExternal: false }
}

export const resolveDrupalMenuPath = (item: IDrupalMenuLink): string =>
  resolveDrupalMenuLink(item).path
